import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Separator } from "design-system/atoms/Separator"
import { cn } from "design-system/utils"
import { formatPercent, isMobileDevice } from "lopermut"
import {
  ArrowRight,
  BookKey,
  Dna,
  Flame,
  Link,
  MessageCircleQuestion,
  MessageSquareText,
  Sparkle,
  Tag,
  Unlink2,
  Users,
} from "lucide-react"
import { RankingBadge } from "modules/shared/components/Miscelanea/RankingBadge"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ClusterDomain, ClusterDomainEnum } from "schema"
import { NodeData } from "./TreemapChart"

export type TooltipData = {
  node: NodeData
  coordinate: { x: number; y: number }
  containerRect: DOMRect | undefined
  segmentId?: "advocates" | "neutrals" | "detractors"
}

type GetRanksForLabel = (
  label?: string,
) => Partial<{ advocates: number; neutrals: number; detractors: number }> | null

function getPotentialBadgeClassNames(key: string) {
  switch (key) {
    case "potentialSolveRate":
      return { border: "border-gradient-support", text: "text-gradient-support" }
    case "knowledgeGapRate":
      return { border: "border-gradient-question", text: "text-gradient-question" }
    case "eligibleRate":
      return {
        border: "border-gradient-action-request",
        text: "text-gradient-action-request",
      }
    default:
      return null
  }
}

type Props = {
  key: string
  domain: ClusterDomain
  tooltip: TooltipData
  scoreComponent: React.ReactNode
  getRanksForLabel?: GetRanksForLabel
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export const TooltipOverlay = ({
  key,
  domain,
  tooltip,
  scoreComponent,
  getRanksForLabel,
  onMouseEnter,
  onMouseLeave,
}: Props) => {
  const { t } = useTranslation()

  const ref = React.useRef<HTMLDivElement | null>(null)
  const [size, setSize] = React.useState({ width: 0, height: 0 })
  const isMobile = useMemo(() => isMobileDevice(), [])

  React.useLayoutEffect(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect()

      // NOTE: Rounding these since values van be with tons of decimals, where comparisons do not
      // work as expected
      const roundedHeight = Math.round(r.height)
      const roundedWidth = Math.round(r.width)

      if (size.width === roundedWidth && size.height === roundedHeight) return

      setSize({ width: roundedWidth, height: roundedHeight })
    }
  }, [size])

  const viewportPadding = 20
  const cursorOffset = 5
  const vw = typeof window !== "undefined" ? window.innerWidth : 0
  const vh = typeof window !== "undefined" ? window.innerHeight : 0
  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)
  const tooltipWidth = Math.min(440, Math.max(360, vw - viewportPadding * 2))

  const maxLeft = Math.max(vw - size.width - viewportPadding, viewportPadding)
  const maxTop = Math.max(vh - size.height - viewportPadding, viewportPadding)

  let left = viewportPadding
  let top = viewportPadding

  if (tooltip.containerRect) {
    const container = tooltip.containerRect
    const centerX = container.left + tooltip.coordinate.x
    const centerY = container.top + tooltip.coordinate.y

    const availableSpace = {
      top: container.top - viewportPadding,
      right: vw - container.right - viewportPadding,
      bottom: vh - container.bottom - viewportPadding,
      left: container.left - viewportPadding,
    }

    type Placement = "top" | "right" | "bottom" | "left"
    const preferredOrder: Placement[] = ["right", "top", "bottom", "left"]

    const fits: Record<Placement, boolean> = {
      top: availableSpace.top >= size.height + cursorOffset,
      right: availableSpace.right >= size.width + cursorOffset,
      bottom: availableSpace.bottom >= size.height + cursorOffset,
      left: availableSpace.left >= size.width + cursorOffset,
    }

    const placement =
      preferredOrder.find((direction) => fits[direction]) ??
      preferredOrder.reduce((best, current) =>
        availableSpace[current] > availableSpace[best] ? current : best,
      )

    switch (placement) {
      case "top": {
        top = container.top - size.height - cursorOffset
        left = clamp(centerX - size.width / 2, viewportPadding, maxLeft)
        break
      }
      case "bottom": {
        top = container.bottom + cursorOffset
        left = clamp(centerX - size.width / 2, viewportPadding, maxLeft)
        break
      }
      case "left": {
        left = container.left - size.width - cursorOffset
        top = clamp(centerY - size.height / 2, viewportPadding, maxTop)
        break
      }
      case "right":
      default: {
        left = container.right + cursorOffset
        top = clamp(centerY - size.height / 2, viewportPadding, maxTop)
        break
      }
    }
  }

  left = clamp(left, viewportPadding, maxLeft)
  top = clamp(top, viewportPadding, maxTop)

  const ranks = getRanksForLabel?.(tooltip.node.name)
  const segmentOrder = ["advocates", "neutrals", "detractors"] as const
  const segmentLabel: Record<(typeof segmentOrder)[number], string> = {
    advocates: t("dashboard.voiceOfCustomer.advocates"),
    neutrals: t("dashboard.voiceOfCustomer.neutrals"),
    detractors: t("dashboard.voiceOfCustomer.detractors"),
  }
  const segmentColor: Record<(typeof segmentOrder)[number], string> = {
    advocates: "positive",
    neutrals: "neutral",
    detractors: "negative",
  }

  const metricBadges = useMemo(() => {
    const badges: Array<{ key: string; icon: React.ReactNode; label: string }> = []
    const isSupport = domain === ClusterDomainEnum.Support
    const isQuestion = domain === ClusterDomainEnum.Question
    const isIssue = domain === ClusterDomainEnum.Issue
    const isActionRequest = domain === ClusterDomainEnum.ActionRequest

    const itemsBadge = (() => {
      if (isSupport || isQuestion) {
        return {
          icon: <MessageCircleQuestion size={14} className="text-muted-foreground" />,
          label: t("dashboard.tooltip.totalQuestions", { count: tooltip.node.totalItems }),
        }
      }

      if (isIssue) {
        return {
          icon: <Tag size={14} className="text-muted-foreground" />,
          label: t("dashboard.tooltip.totalIssues", { count: tooltip.node.totalItems }),
        }
      }

      if (isActionRequest) {
        return {
          icon: <Unlink2 size={14} className="text-muted-foreground" />,
          label: t("dashboard.tooltip.totalActionRequests", { count: tooltip.node.totalItems }),
        }
      }

      return {
        icon: <Dna size={14} className="text-muted-foreground" />,
        label: t("dashboard.tooltip.totalItems", { count: tooltip.node.totalItems }),
      }
    })()

    badges.push({ key: "items", ...itemsBadge })

    badges.push({
      key: "customers",
      icon: <Users size={14} className="text-muted-foreground" />,
      label: t("dashboard.tooltip.customersCount", {
        count: tooltip.node.customersCount ?? 0,
      }),
    })

    const avgSolveRate = tooltip.node.avgSolveRate ?? null
    const avgPotentialSolveRate = tooltip.node.avgPotentialSolveRate ?? null
    if (
      isSupport &&
      avgSolveRate != null &&
      avgPotentialSolveRate != null &&
      avgPotentialSolveRate > 1.2 * avgSolveRate
    ) {
      badges.push({
        key: "potentialSolveRate",
        icon: <BookKey size={14} className="text-support-primary" />,
        label: t("dashboard.tooltip.potentialSolveRate", {
          value: formatPercent({ amount: avgPotentialSolveRate }, true),
        }),
      })
    } else if (!isIssue) {
      badges.push({
        key: "conversations",
        icon: <MessageSquareText size={14} className="text-muted-foreground" />,
        label: t("dashboard.tooltip.conversationsCount", {
          count: tooltip.node.conversationsCount ?? 0,
        }),
      })
    }

    if (isQuestion && avgPotentialSolveRate != null && avgPotentialSolveRate > 0) {
      badges.push({
        key: "knowledgeGapRate",
        icon: <BookKey size={14} className="text-chart-4" />,
        label: t("dashboard.tooltip.knowledgeGapRate", {
          value: formatPercent({ amount: avgPotentialSolveRate }, true),
        }),
      })
    }

    if (isActionRequest && avgPotentialSolveRate != null && avgPotentialSolveRate > 0) {
      badges.push({
        key: "eligibleRate",
        icon: <Link size={14} className="text-chart-3" />,
        label: t("dashboard.tooltip.eligibleRate", {
          value: formatPercent({ amount: avgPotentialSolveRate }, true),
        }),
      })
    }

    return badges
  }, [
    domain,
    t,
    tooltip.node.conversationsCount,
    tooltip.node.customersCount,
    tooltip.node.avgSolveRate,
    tooltip.node.avgPotentialSolveRate,
    tooltip.node.totalItems,
  ])

  const snapshotLabels = useMemo(
    () =>
      Array.from(new Set(tooltip.node.snapshotLabels ?? []))
        .filter(Boolean)
        .slice(0, 6),
    [tooltip.node.snapshotLabels],
  )

  return (
    <div
      ref={ref}
      style={{ position: "fixed", left, top, width: tooltipWidth }}
      className="rounded-md bg-card border border-border p-4 shadow-xl z-50 select-none"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-row gap-2 items-start justify-between w-full">
          <span className="text-md font-medium text-foreground">
            {formatCanonicalTopicLabel(tooltip.node.name)}
          </span>
          <div className="flex flex-row gap-2 items-center">
            {tooltip.node.streakInWeeks > 1 && (
              <Badge
                variant="testing"
                className="flex flex-row gap-2 items-center text-xs w-full whitespace-nowrap"
              >
                <Flame size={16} className="text-testing-1" />
                <span className="text-gradient-testing whitespace-nowrap">
                  {t("dashboard.tooltip.streakInWeeks", {
                    count: tooltip.node.streakInWeeks,
                  })}
                </span>
              </Badge>
            )}
            {tooltip.node.isNewTopic && (
              <Badge
                variant="gradient"
                className="flex flex-row gap-2 items-center text-xs whitespace-nowrap"
              >
                <Sparkle size={16} className="text-chart-1" />
                <span className="text-gradient whitespace-nowrap">
                  {t("dashboard.tooltip.newTopic")}
                </span>
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full">
          <div className="text-xs text-muted-foreground leading-snug line-clamp-4">
            {tooltip.node.description}
          </div>
          {snapshotLabels.length > 0 && (
            <div
              key={`${key}-${domain}-${tooltip.segmentId ?? ""}-${tooltip.node.name ?? ""}-labels`}
              className="flex flex-wrap gap-1 overflow-x-auto"
            >
              {snapshotLabels.map((label, idx) => (
                <span
                  key={`${key}-${label}-${idx}`}
                  className="rounded-xs bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-row gap-6 items-center pt-2 justify-center">
          {metricBadges.length > 0 && (
            <div className="flex flex-col gap-2">
              {metricBadges.map((badge) => {
                const potentialBadgeClassNames = getPotentialBadgeClassNames(badge.key)

                return (
                  <Badge
                    key={badge.key}
                    variant="outline"
                    className={cn(
                      "inline-flex w-fit rounded-full text-xs font-medium gap-2 px-3 py-1",
                      potentialBadgeClassNames?.border,
                    )}
                  >
                    {badge.icon}
                    <span className={cn(potentialBadgeClassNames?.text)}>{badge.label}</span>
                  </Badge>
                )
              })}
            </div>
          )}
          <div className="flex flex-col items-center justify-center">{scoreComponent}</div>
        </div>
        {domain === ClusterDomainEnum.VoiceOfCustomer && ranks && (
          <>
            <Separator />
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {t("dashboard.tooltip.relevance")}
              </span>
              <div className="flex flex-row gap-2 items-center flex-wrap">
                {segmentOrder.map((seg) => {
                  const rank = seg === tooltip.segmentId ? tooltip.node.rank : ranks?.[seg]
                  if (rank == null) return null
                  return (
                    <RankingBadge
                      key={`${key}-${seg}`}
                      rank={rank}
                      label={segmentLabel[seg]}
                      color={segmentColor[seg]}
                    />
                  )
                })}
              </div>
            </div>
          </>
        )}
        {/**
         * NOTE: This allows mobile devices to properly access the filter, as in mobile, when
         * clicking on an element, the default behavior is to show the tooltip instead redirecting
         */}
        {isMobile && (
          <>
            <Separator />
            <Button variant="outline" size="sm">
              <span>
                {domain === ClusterDomainEnum.Support
                  ? t("dashboard.tooltip.goToSupport")
                  : t("dashboard.tooltip.goToConversations")}
              </span>
              <ArrowRight size={16} />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
